import { useState } from 'react';
export { complaintHandle as handle } from '../routeMetadata';
import ConfirmationModal from '../components/ConfirmationModal';

export default function ComplaintPage() {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [orderId, setOrderId] = useState('');
  const [complaint, setComplaint] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const res = await fetch('/.netlify/functions/submitComplaint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, complaint, email, phone }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to submit complaint');
      }

      setShowConfirmation(true);
      setOrderId('');
      setComplaint('');
      setEmail('');
      setPhone('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="container mx-auto px-2 py-12 max-w-4xl">
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        confirmationText="Your complaint has been received, thank you for your feedback!"
        title="Complaint Submitted"
      />
      <div className="bg-white/90 rounded-2xl shadow-xl py-12 px-4 border border-blue-100">
        <h1 className="text-3xl font-bold text-blue-900 mb-4 text-center">File a Complaint</h1>
        <p className="text-gray-700 text-lg mb-8 text-center">
          This is also a good place to report a problem or let us know if you have a suggestion for us to improve our services.
        </p>

        <form onSubmit={handleSubmit} className="max-w-lg mx-auto space-y-6">
          <div>
            <label htmlFor="orderId" className="block text-sm font-medium text-gray-700 mb-1">
              Order ID (optional)
            </label>
            <p className="text-sm text-gray-500 mb-1">If your complaint pertains to a particular order, please fill this out.</p>
            <input
              id="orderId"
              type="text"
              value={orderId}
              onChange={e => setOrderId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Your order ID, if applicable"
            />
          </div>

          <div>
            <label htmlFor="complaint" className="block text-sm font-medium text-gray-700 mb-1">
              Complaint <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-500 mb-1">Please be as detailed as possible.</p>
            <textarea
              id="complaint"
              required
              rows={6}
              value={complaint}
              onChange={e => setComplaint(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
              placeholder="Please describe your complaint in detail..."
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Contact Email Address <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-500 mb-1">Required so we can follow up about your complaint.</p>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Contact Phone Number
            </label>
            <p className="text-sm text-gray-500 mb-1">Optional, but greatly appreciated.</p>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="(555) 555-5555"
            />
          </div>

          {error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting...' : 'Submit Complaint'}
          </button>
        </form>
      </div>
    </div>
  );
}
